/**
 * One key of a pager: a page number, or the arrow either side of them.
 *
 * Shared because the two pagers in the app drifted apart while each kept its
 * own copy — History's keys were 32px, borderless and `text-xs`, the project
 * grid's were 36px, bordered and `text-sm`, and the same control read as two
 * different ones a tab apart (BRAI-730). What a pager puts *in* the keys
 * still differs by screen, which is why this is the key and not the pager.
 *
 * @param label  Accessible name — "Page 4", "Previous page". The children are
 *   a glyph or a bare number, which name the arrows not at all and the pages
 *   ambiguously.
 * @param active Marks the page being shown, visually and as `aria-current`.
 */
export default function PaginationButton({
  children,
  label,
  active = false,
  disabled = false,
  onClick,
}: {
  children: React.ReactNode
  label: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      disabled={disabled}
      onClick={onClick}
      className={[
        'flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-xs font-semibold tabular-nums transition-colors disabled:opacity-40',
        active
          ? 'bg-[var(--accent)] text-white'
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]',
      ].join(' ')}
    >
      {children}
    </button>
  )
}
